import { useEffect } from 'react';

import { LoginFooter } from 'pages/authentication/login/components/LoginFooter';
import { LoginForm } from 'pages/authentication/login/components/LoginForm';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { AuthApi } from 'apis/AuthApi';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/authentication/login/styles/Login.scss';

export const Login = () => {

    const [state, setState] = useSuperState({
        error: '',
        loading: false,
        password: '',
        requireCaptcha: false,
        showPassword: false,
        showSuccess: false,
        successMessage: '',
        turnstileSiteKey: '',
        turnstileToken: '',
        username: ''
    });

    const { error, loading, password, requireCaptcha, showPassword, showSuccess, successMessage, turnstileSiteKey,
        turnstileToken, username } = state;

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { onLoginSuccess } = useApp();
    const { formatMessage } = useIntl();

    const loadConfig = async () => {

        try {

            const config = await AuthApi.GetLoginConfig();
            setState({
                requireCaptcha: config.requireCaptcha,
                turnstileSiteKey: config.turnstileSiteKey
            });

        } catch {

            // Config load failure is non-critical
        }

    };

    useEffect(() => {

        loadConfig();

    }, []);

    // Show a confirmation banner after a successful password-reset redirect
    // (the ResetPassword page navigates here with ?ResetPassword=true).
    useEffect(() => {

        if (searchParams.get('ResetPassword') === 'true') {

            setState({
                showSuccess: true,
                successMessage: formatMessage({ id: 'your_password_has_been_reset_please_sign.c9208cc5', defaultMessage: 'Your password has been reset. Please sign in with your new password.' }) || 'Your password has been reset. Please sign in with your new password.'
            });

        }

    }, [searchParams]);

    // Surface a notice when the API library redirected here because the user's
    // session expired (ApiCaller appends ?SessionExpired=true on a 401).
    useEffect(() => {

        if (searchParams.get('SessionExpired') === 'true') {

            setState({
                error: formatMessage({ id: 'your_session_has_expired_please_sign_in.c5559a0b', defaultMessage: 'Your session has expired. Please sign in again.' })
            });

        }

    }, [searchParams]);

    const handleLogin = async () => {

        if (!username.trim() || !password.trim()) {

            setState({ error: formatMessage({ id: 'username_and_password_are_required.15d0ee7b', defaultMessage: 'Username and password are required.' }) || 'Username and password are required.' });
            return;

        }

        try {

            setState({ error: '', loading: true });

            const response = await AuthApi.Login({
                password: password.trim(),
                turnstileToken: turnstileToken || undefined,
                username: username.trim()
            });
            if (response.success) {

                // Seamless in-SPA handoff: load the app context and flip to the
                // dashboard without a full page reload.
                await onLoginSuccess();

            } else if (response.isLockedOut) {

                window.location.href = '/Access/Lockout.aspx';

            } else if (response.requiresTwoFactor) {

                const returnUrl = new URLSearchParams(window.location.search).get('ReturnUrl') || '';
                window.location.href = `/Access/TwoFactorAuthenticationSignIn?ReturnUrl=${encodeURIComponent(returnUrl)}&RememberMe=false`;

            } else {

                const errorMessage = response.error === 'InvalidCredentials'
                    ? formatMessage({ id: 'the_login_attempt_was_unsuccessful_pleas.4fb96674', defaultMessage: 'The login attempt was unsuccessful. Please try again.' })
                    : response.error === 'NoPartnerAssociated'
                        ? formatMessage({ id: 'your_account_is_not_associated_with_any.791d2009', defaultMessage: 'Your account is not associated with any partner. Please contact your administrator.' })
                        : (response.error || formatMessage({ id: 'login_failed.d11db2da', defaultMessage: 'Login failed.' }));

                setState({
                    error: errorMessage,
                    loading: false,
                    requireCaptcha: response.requiresCaptcha || state.requireCaptcha
                });

            }

        } catch {

            setState({ error: formatMessage({ id: 'an_unexpected_error_occurred_please_try.5f3d4e35', defaultMessage: 'An unexpected error occurred. Please try again.' }) || 'An unexpected error occurred. Please try again.', loading: false });

        }

    };

    const buildNumber = document.getElementById('root')?.dataset.build || '';

    return (
        <div className="login-page">
            <div className="login-form-panel d-flex flex-column position-relative bg-transparent">
                {showSuccess && (
                    <div className="login-success-banner w-100 mx-auto mb-3 d-flex align-items-start gap-3">
                        <i className="fa-solid fa-circle-check login-success-icon flex-shrink-0" />
                        <span className="login-success-body flex-grow-1">
                            {successMessage}
                        </span>
                        <button
                            className="login-success-dismiss"
                            onClick={() => setState({ showSuccess: false, successMessage: '' })}
                            type="button"
                        >
                            <i className="fa-solid fa-xmark" />
                        </button>
                    </div>
                )}

                {error && (
                    <div className="login-error-banner w-100 mx-auto mb-3 d-flex align-items-start gap-3">
                        <i className="fa-solid fa-circle-exclamation login-error-icon flex-shrink-0" />
                        <span className="login-error-body flex-grow-1">
                            {error}
                        </span>
                    </div>
                )}

                <div className="login-form-container d-flex flex-column align-items-center">

                    <div className="login-card w-100 bg-white">
                        <LoginForm
                            error={error}
                            loading={loading}
                            onForgotPassword={() => navigate('/forgot-password')}
                            onForgotUsername={() => navigate('/recover-username')}
                            onPasswordChange={val => setState({ password: val })}
                            onSubmit={handleLogin}
                            onTogglePassword={() => setState({ showPassword: !showPassword })}
                            onTurnstileToken={token => setState({ turnstileToken: token })}
                            onUsernameChange={val => setState({ username: val })}
                            password={password}
                            requireCaptcha={requireCaptcha}
                            showPassword={showPassword}
                            turnstileSiteKey={turnstileSiteKey}
                            username={username}
                        />
                        <div className="login-policy-note w-100 text-center">
                            {<FormattedMessage defaultMessage="Changes have been made to the GMetrix Privacy Policy." id="changes_have_been_made_to_the_gmetrix_pr.c7f15d34" /> || 'Changes have been made to the GMetrix Privacy Policy.'}
                            {' '}
                            <a href="http://gmetrix.com/Home/PrivacyPolicyEducation" rel="noopener noreferrer" target="_blank">
                                {<FormattedMessage defaultMessage="Click here to view." id="click_here_to_view.24e1aed9" /> || 'Click here to view.'}
                            </a>
                        </div>
                    </div>

                </div>

                <LoginFooter buildNumber={buildNumber} />
            </div>

        </div>
    );

};
